import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  ButtonLink,
  CheckTile,
  CheckboxGroup,
  Heading,
  Image,
  NotificationBox,
  Stack,
  Stretch,
  TextLink,
} from '@eneco/sparky';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { GenericCheckboxChoiceStepContent } from './generic-checkbox-choice-step-content.types';
import { Header, Layout, NextButton } from '../../components';
import { useStepContent, useStepKey } from '../../hooks';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { getIllustration } from '../../utils/getIllustration';

interface FormFields {
  [key: string]: string[] | string | undefined;
}

export const GenericCheckboxChoiceStep = () => {
  // This step won't re-render properly when used on multiple consecutive steps
  // without a key that changes per step page. See `useStepKey` for why the
  // rendering's `uid` is not that key.
  return <GenericCheckboxChoice key={useStepKey()} />;
};

const GenericCheckboxChoice: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);

  const textualData = useStepContent<GenericCheckboxChoiceStepContent>('GenericCheckboxChoiceStep');

  const genericFieldName = textualData?.choice?.name ?? '';
  const genericFieldArrayValue = flowContext[genericFieldName as keyof FlowContext] || [];
  const dialog = textualData?.moreInfoDialog;
  // Hoisted so the guard below narrows it: TypeScript cannot carry a narrowing
  // from the surrounding JSX into the `render` closure, but it can through a const.
  const options = textualData?.choice?.genericContentRadio?.options;

  const requiredMessage = textualData?.choice?.requiredMessageText;

  let formSchema = yup.object({
    [genericFieldName]: yup.array(yup.string()),
  });

  if (requiredMessage) {
    formSchema = yup.object({
      [genericFieldName]: yup.array(yup.string()).required(requiredMessage).min(1, requiredMessage),
    });
  }

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [genericFieldName]: genericFieldArrayValue as string[] | undefined,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { nonBlockingValue, blockingValue } = textualData?.choice || {};
  const mutatedGenericField = watch(genericFieldName);

  const showNonBlockingNotification =
    nonBlockingValue &&
    mutatedGenericField?.includes(nonBlockingValue) &&
    textualData?.content?.nonBlockingChoiceNotification?.content;

  const showBlockingNotification =
    blockingValue &&
    mutatedGenericField?.includes(blockingValue) &&
    textualData?.genericError?.errorBlockingChoiceNotification?.content;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const genericFieldValue = formData[genericFieldName];

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          [genericFieldName]: genericFieldValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const onOptionCheckboxChange = (
    name: string,
    value = genericFieldArrayValue as string[],
    onChange: (...event: unknown[]) => void,
  ) => (value.includes(name) ? onChange(value.filter(val => val !== name)) : onChange([...value, name]));

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        {textualData?.content?.title && <Header.Title>{textualData.content.title}</Header.Title>}

        {(dialog?.triggerText || textualData?.content?.content) && (
          <Header.Text>
            <Stack gap="3" direction="column">
              {textualData?.content?.content && <RichText html={textualData.content.content} />}
              {dialog && (
                <TrackedDialog
                  isOpen={isModalOpen}
                  setOpen={setIsModalOpen}
                  trigger={<TextLink emphasis="low">{dialog?.triggerText}</TextLink>}>
                  <Stack gap="6" direction="column">
                    {dialog.image?.default?.src && (
                      <Image
                        src={dialog.image.default.src}
                        alt={dialog.image.alt || ''}
                        aspectRatio="16/9"
                        height="auto"
                        objectFit="cover"
                      />
                    )}

                    {dialog?.title && (
                      <Heading size="S" as="h2">
                        {dialog.title}
                      </Heading>
                    )}

                    <RichText html={dialog.text} />

                    {dialog.buttonLink?.href && (
                      <Stack.Item>
                        <Stretch width={{ initial: true, lg: 'auto' }}>
                          <ButtonLink
                            action="primary"
                            target={dialog.buttonLink.target}
                            href={dialog.buttonLink.href}>
                            {dialog.buttonLink.text}
                          </ButtonLink>
                        </Stretch>
                      </Stack.Item>
                    )}
                  </Stack>
                </TrackedDialog>
              )}
            </Stack>
          </Header.Text>
        )}
      </Header>

      {textualData?.content?.notification?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.variant}
          title={textualData?.content?.notification?.title}
          text={<RichText html={textualData?.content?.notification?.content} />}
        />
      )}

      {options && (
        <Controller
          control={control}
          name={genericFieldName}
          render={({ field: { value, onChange } }) => (
            <CheckboxGroup label="" error={errors?.[genericFieldName]?.message}>
              {options.map(({ name, label, illustration }) => {
                const Illustration = getIllustration(illustration);
                return (
                  <CheckTile
                    key={name}
                    name={name ?? ''}
                    icon={Illustration && <Illustration size="small" color="currentColor" />}
                    isChecked={value?.includes(name ?? '')}
                    onChange={() => onOptionCheckboxChange(name ?? '', value as string[], onChange)}>
                    {label ?? ''}
                  </CheckTile>
                );
              })}
            </CheckboxGroup>
          )}
        />
      )}

      {showNonBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.nonBlockingChoiceNotification?.variant}
          title={textualData?.content?.nonBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.content?.nonBlockingChoiceNotification?.content} />}
        />
      )}

      {!showBlockingNotification && (
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText}</NextButton>
      )}

      {showBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.genericError?.errorBlockingChoiceNotification?.variant}
          title={textualData?.genericError?.errorBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.genericError?.errorBlockingChoiceNotification?.content} />}
        />
      )}
    </Layout.Content>
  );
};
